import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Send, X } from "lucide-react";

/**
 * Reusable Telegram send-to-customer button.
 *
 * Props:
 *   - customer_id: string (required)
 *   - defaultMessage: string  (pre-populated composable text)
 *   - kind: string  (e.g. "production_status", "shipment_ready")
 *   - entity_ref: string (production no, shipment no, etc.)
 *   - label: string  (button label, defaults to "Telegram")
 *   - size: "sm" | "md"
 *
 * Behavior:
 *   1. Click → opens confirmation modal with editable textarea (defaultMessage)
 *   2. Shows customer name + link status (opt-in required)
 *   3. On "Gönder" → POST /api/telegram/notify-customer
 *   4. Alert result. No sending without explicit user confirmation.
 */
export default function TelegramSendButton({ customer_id, defaultMessage, kind = "customer_status", entity_ref, label = "Telegram", size = "sm", testid }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(defaultMessage || "");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setText(defaultMessage || ""); }, [defaultMessage]);
  useEffect(() => {
    if (!open || !customer_id) return;
    setStatus(null);
    api.get(`/telegram/customer/${customer_id}/status`)
      .then(setStatus).catch(e => setStatus({ error: e.response?.data?.detail || "Durum alınamadı" }));
  }, [open, customer_id]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/telegram/notify-customer", { customer_id, text: text.trim(), kind, entity_ref });
      if (r.ok) alert(`✅ Telegram üzerinden gönderildi (${r.customer_name})`);
      else alert(`⚠ Gönderilemedi: ${r.response?.description || JSON.stringify(r.response || r).slice(0, 200)}`);
      setOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Gönderilemedi");
    } finally { setBusy(false); }
  };

  const btnClass = size === "sm"
    ? "inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 hover:border-sky-400 transition"
    : "inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 hover:border-sky-400 transition font-semibold";

  return (
    <>
      <button onClick={() => setOpen(true)} className={btnClass} title="Müşteriye Telegram ile bildirim gönder" data-testid={testid || `tg-send-${kind}`}>
        <TelegramIcon size={size === "sm" ? 11 : 14}/> {label}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-lg w-full p-5" onClick={e => e.stopPropagation()} data-testid="tg-send-modal">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="bg-sky-100 dark:bg-sky-900/40 p-2 rounded-lg"><TelegramIcon size={18} className="text-sky-600"/></div>
                <div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Müşteriye Telegram Bildirimi</div>
                  <div className="text-xs text-slate-500">{status?.customer_name || "…"}{entity_ref && ` · ${entity_ref}`}</div>
                </div>
              </div>
              <button onClick={() => !busy && setOpen(false)} className="p-1 text-slate-400 hover:text-slate-700" data-testid="tg-send-close"><X size={16}/></button>
            </div>

            {status && !status.error && !status.can_notify && (
              <div className="mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded p-2.5 text-xs text-amber-800 dark:text-amber-300" data-testid="tg-send-warn">
                ⚠ Bu müşteri Telegram'a bağlı değil.<br/>
                <b>Bağlamak için:</b> Sistem Ayarları > Telegram Bot > "Müşteriye Bağlantı Kodu Ver" ile kod oluşturup müşterinize iletin. Bağlantı yapılmadan mesaj gönderilemez.
              </div>
            )}
            {status?.error && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded p-2.5 text-xs text-red-800">{status.error}</div>
            )}
            {status?.can_notify && (
              <div className="mb-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-500/30 rounded p-2 text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5" data-testid="tg-send-ok">
                ✓ Müşteri bağlı — mesaj @{status.customer_name}'e iletilecek. Chat ID: <code>{status.chat_id}</code>
              </div>
            )}

            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Mesaj (düzenleyebilirsiniz)</label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={6}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded resize-y font-mono"
              placeholder="Mesaj metni…" data-testid="tg-send-textarea"/>

            <div className="flex justify-between items-center mt-3">
              <span className="text-[10px] text-slate-400">{text.length} / 4090 karakter</span>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700" data-testid="tg-send-cancel">Vazgeç</button>
                <button onClick={send} disabled={busy || !text.trim() || !status?.can_notify}
                  className="text-xs px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white font-semibold flex items-center gap-1.5"
                  data-testid="tg-send-confirm">
                  <Send size={12}/> {busy ? "Gönderiliyor..." : "Gönder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Simple inline Telegram paper-plane icon (SVG) — avoids new dependency
export function TelegramIcon({ size = 14, className = "" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M21.94 4.4L2.5 12.2c-.7.3-.7 1.3.1 1.5l4.9 1.5 1.9 6c.2.6 1 .8 1.5.3l2.7-2.5 4.9 3.6c.6.4 1.5.1 1.7-.6l3.2-15.6c.2-1-.7-1.8-1.55-1.5zM10 15l-.5 3.5-1.2-3.8 10.3-8.2L10 15z"/>
    </svg>
  );
}
